import {Badge, SxProps, Theme} from "@mui/material";
import {DateCalendar} from "@mui/x-date-pickers/DateCalendar";
import {PickersDay, PickersDayProps} from "@mui/x-date-pickers/PickersDay";
import {DateTime} from "luxon";

type SpecialDayMarkerInfo = {
    day: string;
    everyYear: boolean;
};

export const getFormattedDate = (dateToFormat: Date): string => {
    const year = dateToFormat.getFullYear();
    let month = (1 + dateToFormat.getMonth()).toString();
    month = month.length > 1 ? month : "0" + month;
    let day = dateToFormat.getDate().toString();
    day = day.length > 1 ? day : "0" + day;
    return year + "-" + month + "-" + day;
};

export const sameDate = (date1: DateTime, date2: DateTime): boolean => {
    return date1.year === date2.year && date1.month === date2.month && date1.day === date2.day;
};

export const sameMonthAndDay = (date1: DateTime, date2: DateTime): boolean => {
    return date1.month === date2.month && date1.day === date2.day;
};

const getDaySelectFilter = (day: DateTime, showEveryYear: boolean): ((markerInfo: SpecialDayMarkerInfo) => boolean) => {
    return (markerInfo: SpecialDayMarkerInfo) => {
        const infoDate = DateTime.fromISO(markerInfo.day);
        if (showEveryYear && markerInfo.everyYear) return sameMonthAndDay(day, infoDate);
        else return sameDate(day, infoDate);
    };
};

function SpecialDay(
    props: PickersDayProps<DateTime> & {specialDays?: SpecialDayMarkerInfo[]; showEveryYear?: boolean},
) {
    const {specialDays = [], showEveryYear = false, day, outsideCurrentMonth, ...other} = props;

    const isSpecialDay = !outsideCurrentMonth && specialDays.filter(getDaySelectFilter(day, showEveryYear)).length > 0;

    if (isSpecialDay)
        return (
            <Badge key={props.day.toString()} variant="dot" color="secondary">
                <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
            </Badge>
        );
    else return <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />;
}

type Props = {
    value: Date;
    onChange: (selectedDate: Date, specialDay?: Date) => void;
    specialDays: SpecialDayMarkerInfo[];
    disabled?: boolean;
    sx?: SxProps<Theme>;

    /** If true, the date picker shows past and future special days that are marked every year.
     * Although this should be the default, not every component is ready for it yet, only the
     * global special days page. Once all components are ready, this argument can be removed and
     * used as the default.
     **/
    // TODO: adjust all components to support this, then remove this argument
    showEveryYear?: boolean;
};

export default function SpecialDayPicker({onChange, specialDays, sx, disabled = false, showEveryYear = false}: Props) {
    return (
        <DateCalendar
            // setting the value results in a disabled calendar - probably a bug in DateCalendar
            // value={DateTime.fromJSDate(value)}
            onChange={(date: DateTime) => {
                const specialDay = specialDays.find((d) => {
                    if (d.everyYear) return sameMonthAndDay(date, DateTime.fromISO(d.day));
                    return date === DateTime.fromISO(d.day);
                });
                const specialDayDate = specialDay ? DateTime.fromISO(specialDay.day).toJSDate() : undefined;

                onChange(date.toJSDate(), specialDayDate);
            }}
            disabled={disabled}
            slots={{
                day: SpecialDay,
            }}
            slotProps={{
                day: {
                    specialDays,
                    showEveryYear,
                } as any,
            }}
            sx={sx}
        />
    );
}
